import { HttpExecutionContext, HttpResponse, type HttpPlugin } from "@kalodata/http";
import { applyLegacyRequestHeaders } from "./applyLegacyRequestHeaders";
import { applyLegacyRequestQuery } from "./applyLegacyRequestQuery";
import { reportHttpRequestSuccess } from "./reportHttpEvent";
import { resolveTraceIdFromHeaders } from "./traceId";
import type { ClipHttpContext, ClipHttpEnvelope, ClipHttpMeta } from "./types";
import { LOCALSTORAGE_KEY } from "@config";
import { message } from "antd";
import { ClipErrorCode, getErrorMessageWithOptions } from "@utils/errrorMessage";
import { t } from "i18next";
import { copyTextToClipboard } from "@kalodata/tools";

export const DEFAULT_CLIP_TIMEOUT_MS = 60 * 1000;

export const isBusinessError = (response: HttpResponse<unknown, ClipHttpMeta, ClipHttpContext>) => {
  const responseData = response.data as ClipHttpEnvelope;
  return response.status === 200 && !!responseData && responseData.success === false;
};

export const createHttpRequestReportSuccessPlugin = (): HttpPlugin<ClipHttpMeta, ClipHttpContext> => {
  return {
    name: "httpRequestLifecycleReport",
    onResponse: (response, context) => {
      if (response.status === 200) {
        reportHttpRequestSuccess(context.request, resolveTraceIdFromHeaders(response.headers));
      }
      return response;
    },
  };
};

export const createLegacyHeadersPlugin = (): HttpPlugin<ClipHttpMeta, ClipHttpContext> => {
  return {
    name: "legacyHeaders",
    onRequest: (request) => applyLegacyRequestHeaders(request),
  };
};

export const createLegacyQueryPlugin = (): HttpPlugin<ClipHttpMeta, ClipHttpContext> => {
  return {
    name: "legacyQuery",
    onRequest: (request) => applyLegacyRequestQuery(request),
  };
};

export const createAttachAccessTokenPlugin = (): HttpPlugin<ClipHttpMeta, ClipHttpContext> => {
  return {
    name: "attachAccessToken",
    onRequest: (request) => {
      const accessToken = localStorage.getItem(LOCALSTORAGE_KEY.ACCESS_TOKEN);
      if (!accessToken) return request;
      return {
        ...request,
        headers: {
          ...request.headers,
          Authorization: `Bearer ${accessToken}`,
        },
      };
    },
  };
};

export const createToastErrorPlugin = (): HttpPlugin<ClipHttpMeta, ClipHttpContext> => {
  return {
    name: "toastError",
    onError: (error, exe_context: HttpExecutionContext<ClipHttpMeta, ClipHttpContext>) => {
      const {
        request: { context },
      } = exe_context;
      if (!context?.silent) {
        const { code, message: errorMessage, status } = error;
        const codeMsg = code ? getErrorMessageWithOptions(code as unknown as ClipErrorCode) : null;
        const finalMsg = codeMsg?.message || errorMessage || `Request failed: ${code || status || "Unknown error"}`;
        const finalMsgOptions = codeMsg?.options;
        const traceId = error.traceId || resolveTraceIdFromHeaders(error.response?.headers);
        const copyTraceId = () => {
          if (!traceId) return;
          copyTextToClipboard(traceId)
            .then(() => {
              message.success(t("Copied"));
            })
            .catch(() => {});
        };

        message.error({
          content: (
            <span className="inline-flex flex-col">
              <span>{t(finalMsg, finalMsgOptions)}</span>
              {traceId && (
                <span className="select-all cursor-pointer" title={t("Click to copy")} onClick={copyTraceId}>
                  {`Trace ID: ${traceId}`}
                </span>
              )}
            </span>
          ),
          duration: context?.toastDuration ?? 5,
        });
      }
      throw error;
    },
  };
};
